import { InjectionToken } from '@angular/core';

import { io, Socket } from 'socket.io-client';

import { WebSocketOptionsService } from './web-socket-options.service';

export type WebSocketConnectionStatus = 'CONNECTED' | 'OFFLINE' | 'DISABLED';

export class WebSocketConnection {
    constructor(private socket: Socket) {}

    setCallback(eventName: string, callback: (data: string) => any): void {
        if (this.socket) {
            this.socket.on(eventName, callback);
        }
    }

    disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
        }
    }

    hasConnection(): boolean {
        return this.socket && this.socket.connected;
    }
}

/**
 * Service to establish a web socket connection.
 * @export
 */
export class WebSocketConnectionService {
    /**
     * @property serverAddress - base url of connections that will be established by this service
     */
    serverAddress: string;

    /**
     * @property optionsService - service injected responsible for defining the web socket options
     */
    optionsService: WebSocketOptionsService;

    /**
     * Creates an instance of WebSocketOptionsService.
     * @param optionsService
     */
    constructor(optionsService: WebSocketOptionsService, serverAddress: string) {
        this.serverAddress = serverAddress;
        this.optionsService = optionsService;
    }

    /**
     * Perform Establish a socket connection
     */
    async connect(partialUrl?: string): Promise<WebSocketConnection> {
        const url = partialUrl ? this.buildUrl(this.serverAddress, partialUrl) : this.serverAddress;
        const opt = await this.requestOptions();

        return new WebSocketConnection(io(url, opt));
    }

    private buildUrl(...urlParts: string[]): string {
        // sanitize each url part
        urlParts = urlParts.map((part) => {
            // verify if the string exists
            if (!part) {
                return null;
            }

            // remove leading and trailing whitespace
            // and verify if it is not empty
            part = part.trim();
            if (part === '') {
                return null;
            }

            // verify if the first char is not a slash
            if (part[0] === '/') {
                part = part.substr(1, part.length - 1);
            }

            // verify if the last char is not a slash
            if (part[part.length - 1] === '/') {
                part = part.substr(0, part.length - 1);
            }

            return part;
        });

        // remove null elements
        urlParts = urlParts.filter((part) => part !== null);

        // joint all the components
        return urlParts.join('/');
    }

    private async requestOptions(): Promise<any> {
        await this.optionsService.setAuthorizationOptions();
        return this.optionsService.options;
    }
}

export const WEBSOCKET_REQUEST_SERVICE_TOKEN = new InjectionToken<string>('request service token');

/**
 * Factory to provide the [WebSocketConnectionService]{@link WebSocketConnectionService}
 */
export function PROVIDE_WEBSOCKET_CONNECTION_SERVICE_ENDPOINT_FACTORY(
    optionsService: WebSocketOptionsService,
    serverAddress: string
): WebSocketConnectionService {
    return new WebSocketConnectionService(optionsService, serverAddress);
}
